import React from 'react'
import { Link } from 'react-router-dom'
import { SERVICES_DATA } from '../data/siteData'
import { useCMSCollection } from '../hooks/useCMS'

export function CorePillars() {
  const { data: cmsServices } = useCMSCollection('ydelser', SERVICES_DATA)

  const krop =
    cmsServices?.find((s) => s.slug?.da?.includes('krop') || s.title?.toLowerCase().includes('krop')) ||
    SERVICES_DATA[0]
  const coach =
    cmsServices?.find((s) => s.slug?.da?.includes('coach') || s.title?.toLowerCase().includes('coach')) ||
    SERVICES_DATA[1]
  const hypno =
    cmsServices?.find((s) => s.slug?.da?.includes('hypno') || s.title?.toLowerCase().includes('hypno')) ||
    SERVICES_DATA[2]

  const kropImage = Array.isArray(krop?.image) ? krop.image[0] : krop?.image || SERVICES_DATA[0].image
  const coachImage = Array.isArray(coach?.image) ? coach.image[0] : coach?.image || SERVICES_DATA[1].image
  const hypnoImage = Array.isArray(hypno?.image) ? hypno.image[0] : hypno?.image || SERVICES_DATA[2].image

  return (
    <section className="py-20 lg:py-28 bg-[#FAF8F5]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-[#1b3a4b]/8 text-[#1b3a4b] text-xs font-semibold uppercase tracking-wider mb-4">
            <span className="w-1.5 h-1.5 rounded-full bg-[#7c875a]" />
            Tre komplementære tilgange
          </div>
          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-bold text-[#1b3a4b] tracking-tight">
            Kropsterapi · Coaching · Hypnoterapi
          </h2>
          <p className="mt-4 text-base sm:text-lg text-[#64748B] leading-relaxed">
            Med udgangspunkt i forbindelsen mellem krop og psyke anskuer Bo Steendahl mennesket som en helhed. Sammen skaber de tre områder en dyb og holdbar forandring indefra.
          </p>
        </div>

        {/* Three-column feature/service row (Design Reference) */}
        <div className="max-w-[768px] lg:max-w-none mx-auto grid grid-cols-1 lg:grid-cols-3 gap-8 lg:gap-10 items-stretch">
          {/* Column 1: Kropsterapi */}
          <div className="flex flex-col items-center text-center group">
            {/* Landscape-oriented image at top with rounded corners */}
            <div className="w-full aspect-[4/3] sm:aspect-[16/11] lg:aspect-[4/3] rounded-2xl overflow-hidden shadow-sm border border-[#E5E0D8] bg-[#1b3a4b]/5 mb-6">
              <img
                src={kropImage}
                alt="Totum Kropsterapi ved Bo Steendahl"
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
              />
            </div>
            {/* Vertically centered text block */}
            <div className="flex-1 flex flex-col items-center justify-between">
              <div className="space-y-3">
                <h3 className="font-serif text-xl sm:text-2xl font-bold text-[#1b3a4b]">
                  {krop.title || 'Totum Kropsterapi'}
                </h3>
                <p className="text-sm sm:text-base text-[#64748B] leading-relaxed max-w-sm">
                  {krop.summary || 'Genfind balancen i krop, sind og tanker. Gennem pulserende massage, guidet vejrtrækning og akupressur frigøres gamle spændinger og stress.'}
                </p>
              </div>
              <div className="pt-6 mt-auto">
                <Link
                  to="/kropsterapi"
                  className="inline-block font-semibold text-sm sm:text-base text-[#1b3a4b] border-b-2 border-[#1b3a4b] pb-0.5 hover:text-[#7c875a] hover:border-[#7c875a] transition-colors"
                >
                  Læs mere om kropsterapi
                </Link>
              </div>
            </div>
          </div>

          {/* Column 2: Coaching */}
          <div className="flex flex-col items-center text-center group">
            {/* Landscape-oriented image at top with rounded corners */}
            <div className="w-full aspect-[4/3] sm:aspect-[16/11] lg:aspect-[4/3] rounded-2xl overflow-hidden shadow-sm border border-[#E5E0D8] bg-[#1b3a4b]/5 mb-6">
              <img
                src={coachImage}
                alt="Mindset Coaching ved Bo Steendahl"
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
              />
            </div>
            {/* Vertically centered text block */}
            <div className="flex-1 flex flex-col items-center justify-between">
              <div className="space-y-3">
                <h3 className="font-serif text-xl sm:text-2xl font-bold text-[#1b3a4b]">
                  {coach.title || 'Mindset Coaching'}
                </h3>
                <p className="text-sm sm:text-base text-[#64748B] leading-relaxed max-w-sm">
                  {coach.summary || 'Skab kontakt mellem tanker, sind og krop, synliggør dine indre ressourcer og bryd ud af de vaner og mønstre, som holder dig fast.'}
                </p>
              </div>
              <div className="pt-6 mt-auto">
                <Link
                  to="/coaching"
                  className="inline-block font-semibold text-sm sm:text-base text-[#1b3a4b] border-b-2 border-[#1b3a4b] pb-0.5 hover:text-[#7c875a] hover:border-[#7c875a] transition-colors"
                >
                  Læs mere om coaching
                </Link>
              </div>
            </div>
          </div>

          {/* Column 3: Hypnoterapi */}
          <div className="flex flex-col items-center text-center group">
            {/* Landscape-oriented image at top with rounded corners */}
            <div className="w-full aspect-[4/3] sm:aspect-[16/11] lg:aspect-[4/3] rounded-2xl overflow-hidden shadow-sm border border-[#E5E0D8] bg-[#1b3a4b]/5 mb-6">
              <img
                src={hypnoImage}
                alt="Hypnoterapi ved Bo Steendahl"
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
              />
            </div>
            {/* Vertically centered text block */}
            <div className="flex-1 flex flex-col items-center justify-between">
              <div className="space-y-3">
                <h3 className="font-serif text-xl sm:text-2xl font-bold text-[#1b3a4b]">
                  {hypno.title || 'Hypnoterapi'}
                </h3>
                <p className="text-sm sm:text-base text-[#64748B] leading-relaxed max-w-sm">
                  {hypno.summary || 'Underbevidstheden styrer 95 % af vores handlinger. Gennem tryghed, tillid og fokus skabes vedvarende forandring i det ubevidste sind.'}
                </p>
              </div>
              <div className="pt-6 mt-auto">
                <Link
                  to="/hypnoterapi"
                  className="inline-block font-semibold text-sm sm:text-base text-[#1b3a4b] border-b-2 border-[#1b3a4b] pb-0.5 hover:text-[#7c875a] hover:border-[#7c875a] transition-colors"
                >
                  Læs mere om hypnoterapi
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}
